/**
 * Putting ties on, and taking them off.
 *
 * A tie is two flags, one on each head it joins, and which two heads make one tie is not
 * decided here: `document/ties.ts` follows every tie of a part, and validation and playback
 * read the same answer. So both commands ask it. A tie is only written where the rule will
 * follow it from the one head to the other, and one is taken off the head the rule paired it
 * with -- which is how the editor cannot leave behind half a tie that it would then report.
 */

import type { EventId, PartId } from "../document/ids";
import { locateEvent } from "../document/query";
import type { EventLocation } from "../document/query";
import type { NotePitch } from "../document/pitch";
import { headName, tieKey, tiesOf } from "../document/ties";
import type { HeadAt, Ties } from "../document/ties";
import type { NoteEvent, Score } from "../document/types";
import type { Command } from "../commands/types";
import { sameVoice } from "./lines";
import { selectedNotes } from "./selection";
import type { Selection } from "./selection";

/** The heads of events as an edit would leave them, by the event they belong to. */
type Rewritten = Map<EventId, readonly NotePitch[]>;

/**
 * The next event of the same voice, crossing the bar line; `null` at the end of the line.
 *
 * A voice belongs to its bar, so across the bar line it is carried as the caret is -- the
 * same voice of the same staff -- and not by its index in the bar it left.
 */
function nextInVoice(score: Score, at: EventLocation): EventLocation | null {
  const part = score.parts.find((candidate) => candidate.id === at.partId);
  let from = part?.measures[at.measureId];
  if (part === undefined || from === undefined) return null;
  const following = from.voices[at.voiceIndex]?.events[at.eventIndex + 1];
  if (following !== undefined) return locateEvent(score, following.id);
  let voiceIndex = at.voiceIndex;
  for (let index = at.measureIndex + 1; index < score.measures.length; index += 1) {
    const global = score.measures[index];
    const into = global === undefined ? undefined : part.measures[global.id];
    if (into === undefined) continue;
    voiceIndex = sameVoice(from, into, voiceIndex);
    from = into;
    const first = into.voices[voiceIndex]?.events[0];
    if (first !== undefined) return locateEvent(score, first.id);
  }
  return null;
}

/** Rewrite one head of one event, on top of whatever the edit has already done to it. */
function rewrite(
  heads: Rewritten,
  event: NoteEvent,
  place: number,
  flags: Partial<Pick<NotePitch, "tieStart" | "tieStop">>,
): void {
  const now = heads.get(event.id) ?? event.pitches;
  heads.set(
    event.id,
    now.map((head, index) => (index === place ? { ...head, ...flags } : head)),
  );
}

function commandsOf(heads: Rewritten): Command[] {
  return [...heads].map(([eventId, pitches]) => ({ kind: "setPitches", eventId, pitches }));
}

/** One tie the selection asks for: this head of this event into that head of the next. */
interface Wanted {
  readonly partId: PartId;
  readonly from: NoteEvent;
  readonly head: number;
  readonly into: NoteEvent;
  readonly stop: number;
}

function written(wanted: readonly Wanted[]): Rewritten {
  const heads: Rewritten = new Map();
  for (const each of wanted) {
    rewrite(heads, each.from, each.head, { tieStart: true });
    rewrite(heads, each.into, each.stop, { tieStop: true });
  }
  return heads;
}

/**
 * Tie every selected note into the note that follows it.
 *
 * A tie joins two soundings of the same pitch, so only the heads that appear in both
 * events are tied: tying a C-E-G chord into a C-E chord ties the C and the E and leaves
 * the G to stop, which is what the notation means and what playback then does. The same
 * pitch is the same sound, whichever way each note spells it.
 *
 * The ties are tried before they are written. Where the voice is silent between the two
 * notes and another voice sounds the pitch in that silence, the rule that follows ties would
 * not carry this one across, so it is not made.
 */
export function tie(score: Score, selection: Selection): Command | string {
  const wanted: Wanted[] = [];
  for (const at of selectedNotes(score, selection)) {
    const next = nextInVoice(score, at);
    if (next === null || next.event.kind !== "note") continue;
    const into = next.event;
    at.event.pitches.forEach((head, index) => {
      const stop = into.pitches.findIndex((other) => tieKey(other) === tieKey(head));
      if (stop >= 0) wanted.push({ partId: at.partId, from: at.event, head: index, into, stop });
    });
  }

  const tried = written(wanted);
  const followed = new Map<PartId, Ties>();
  for (const part of score.parts) {
    if (wanted.some((each) => each.partId === part.id)) {
      followed.set(part.id, tiesOf(score, part, tried));
    }
  }
  const kept = wanted.filter((each) => {
    const reached = followed.get(each.partId)?.into.get(headName(each.from.id, each.head)) ?? [];
    return reached.some((end) => end.eventId === each.into.id && end.pitch === each.stop);
  });
  if (kept.length === 0) return "there is no note of the same pitch to tie into";
  return { kind: "batch", label: "tie", commands: commandsOf(written(kept)) };
}

/** One end of a tie being taken off: the head, and which of its two flags the tie is. */
interface End {
  readonly ties: Ties;
  readonly head: HeadAt;
  readonly flag: "tieStart" | "tieStop";
}

function nameOf(head: HeadAt): string {
  return headName(head.eventId, head.pitch);
}

/** The name of one tie, by the head it starts on and the head it ends on. */
function tieName(start: string, stop: string): string {
  return `${start}>${stop}`;
}

/**
 * Take the ties off the selection: off its notes, and off the heads they were tied to on
 * either side.
 *
 * The head at the other end of a tie is the one the rule paired it with, wherever in the
 * part that is, and a head beside it that is tied from somewhere else keeps its tie. A head
 * may be one end of two ties -- the note before a first- and second-time bar is tied into
 * both -- and keeps its flag until the last of them is taken off, so that the one left is
 * still whole.
 */
export function untie(score: Score, selection: Selection): Command | string {
  const heads: Rewritten = new Map();
  const followed = new Map<PartId, Ties>();
  // The ties taken off, and the heads at their far ends.
  const off = new Set<string>();
  const ends: End[] = [];
  for (const at of selectedNotes(score, selection)) {
    const part = score.parts.find((candidate) => candidate.id === at.partId);
    if (part === undefined) continue;
    const ties = followed.get(part.id) ?? tiesOf(score, part);
    followed.set(part.id, ties);
    at.event.pitches.forEach((head, index) => {
      const name = headName(at.event.id, index);
      for (const stop of ties.into.get(name) ?? []) {
        off.add(tieName(name, nameOf(stop)));
        ends.push({ ties, head: stop, flag: "tieStop" });
      }
      for (const start of ties.from.get(name) ?? []) {
        off.add(tieName(nameOf(start), name));
        ends.push({ ties, head: start, flag: "tieStart" });
      }
      if (head.tieStart || head.tieStop) {
        rewrite(heads, at.event, index, { tieStart: false, tieStop: false });
      }
    });
  }

  for (const { ties, head, flag } of ends) {
    const name = nameOf(head);
    const whole =
      flag === "tieStop"
        ? (ties.from.get(name) ?? []).map((start) => tieName(nameOf(start), name))
        : (ties.into.get(name) ?? []).map((stop) => tieName(name, nameOf(stop)));
    const event = locateEvent(score, head.eventId)?.event;
    if (event?.kind !== "note" || !whole.every((each) => off.has(each))) continue;
    rewrite(heads, event, head.pitch, { [flag]: false });
  }
  if (heads.size === 0) return "nothing selected is tied";
  return { kind: "batch", label: "untie", commands: commandsOf(heads) };
}
